import { Fragment, type ReactNode } from 'react';

export interface Column<T> {
  key: keyof T | string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  isLoading?: boolean;
  isError?: boolean;
  emptyMessage?: string;
  rowKey: (row: T) => string;
  onRetry?: () => void;
  /** Conteúdo extra renderizado abaixo da row (expansion panel). */
  renderRowExtra?: (row: T) => ReactNode;
  /**
   * Aperta a tabela: letra e espaçamento menores, pra tabela larga caber na
   * tela sem arrastar pro lado. Só pra quem tem coluna demais, como a de
   * conversas. As outras seguem no tamanho confortável.
   */
  dense?: boolean;
}

export function DataTable<T>(props: DataTableProps<T>): JSX.Element {
  const {
    columns,
    rows,
    isLoading,
    isError,
    emptyMessage,
    rowKey,
    onRetry,
    renderRowExtra,
    dense,
  } = props;

  const tamanhoTexto = dense ? 'text-xs' : 'text-sm';
  const espacoCelula = dense ? 'px-2 py-2' : 'px-4 py-3';

  if (isLoading) {
    return (
      <div className="card animate-pulse" role="status" aria-live="polite">
        <div className="h-4 bg-sara-surface-hover rounded w-40 mb-3" />
        <div className="h-4 bg-sara-surface-hover rounded w-full mb-2" />
        <div className="h-4 bg-sara-surface-hover rounded w-full mb-2" />
        <div className="h-4 bg-sara-surface-hover rounded w-3/4" />
      </div>
    );
  }

  if (isError) {
    return (
      <div role="alert" className="card border-sara-danger">
        <p className="text-sara-danger mb-2">Erro carregando os dados.</p>
        {onRetry && (
          <button type="button" className="btn-ghost" onClick={onRetry}>
            Tentar de novo
          </button>
        )}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="card text-sara-text-muted text-sm">
        {emptyMessage ?? 'Nenhum registro encontrado.'}
      </div>
    );
  }

  return (
    <div className="card overflow-x-auto p-0">
      <table className={`w-full ${tamanhoTexto}`}>
        <thead className="bg-sara-surface-hover/50 text-sara-text-muted text-left">
          <tr>
            {columns.map((c) => (
              <th key={String(c.key)} className={`${espacoCelula} font-medium`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const extra = renderRowExtra?.(row);
            return (
              <Fragment key={rowKey(row)}>
                <tr className="border-t border-sara-border/60 hover:bg-sara-surface-hover/50">
                  {columns.map((c) => (
                    <td key={String(c.key)} className={`${espacoCelula} align-top`}>
                      {c.render
                        ? c.render(row)
                        : String((row as Record<string, unknown>)[String(c.key)] ?? '')}
                    </td>
                  ))}
                </tr>
                {extra && (
                  <tr className="border-t border-sara-border/40">
                    <td colSpan={columns.length} className="p-0">
                      {extra}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
